/**
 * Shared reason-code dropdown + conditional free-text field. A backend
 * reason_code taxonomy always requires a code, and free text only when
 * reason_code === 'other' — this component enforces that shape so callers
 * just read `{ reasonCode, reason }` and know it's valid once
 * `isReasonValid` is true.
 *
 * Generic over the taxonomy: pass whatever `codes` list applies. For order
 * actions, that's `selectableReasonCodes(action)` from orderActionReasons.ts
 * — NOT `ORDER_ACTION_REASON_CODES[action]` directly, which includes
 * system-only codes (e.g. the refire flow's automatic cancel of the
 * original fill) no human should be offered. A different domain (e.g. the
 * patient profile builder) just passes its own codes list. `labelFor`
 * renders human-readable option text. Used by ConfirmActionCard +
 * CustomerTabs (order actions) and the patient profile builder.
 */

export interface ReasonValue {
  reasonCode: string;
  reason: string;
}

export function isReasonValid({ reasonCode, reason }: ReasonValue): boolean {
  if (!reasonCode) return false;
  if (reasonCode === 'other') return reason.trim().length > 0;
  return true;
}

function defaultLabel(code: string): string {
  return code;
}

export function ReasonPicker({
  codes,
  value,
  onChange,
  disabled,
  label = 'Reason',
  labelFor = defaultLabel,
}: {
  codes: string[];
  value: ReasonValue;
  onChange: (next: ReasonValue) => void;
  disabled?: boolean;
  label?: string;
  labelFor?: (code: string) => string;
}) {
  return (
    <div className="flex flex-col gap-8">
      <label className="flex flex-col gap-4">
        <span className="font-mono uppercase text-12 text-ink-muted">
          {label}
        </span>
        <select
          value={value.reasonCode}
          onChange={(e) =>
            onChange({ reasonCode: e.target.value, reason: value.reason })
          }
          disabled={disabled}
          className="h-36 px-10 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink"
        >
          <option value="">Select a reason…</option>
          {codes.map((code) => (
            <option key={code} value={code}>
              {labelFor(code)}
            </option>
          ))}
        </select>
      </label>
      {value.reasonCode === 'other' && (
        <label className="flex flex-col gap-4">
          <span className="font-mono uppercase text-12 text-ink-muted">
            Details
          </span>
          <textarea
            value={value.reason}
            onChange={(e) =>
              onChange({ reasonCode: value.reasonCode, reason: e.target.value })
            }
            disabled={disabled}
            rows={3}
            placeholder="Describe the reason — required when 'Other' is selected"
            className="px-10 py-8 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink resize-y"
          />
        </label>
      )}
    </div>
  );
}
